import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { mockData } from '../data/mockData';

const ContactSection = ({ onContactClick }) => {
  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
              Get in Touch
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to start your trademark registration? Contact us today for a free consultation.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h3 className="text-2xl font-bold text-blue-900 mb-6">
                Contact Information
              </h3>
              
              <div className="space-y-6 mb-8">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-blue-900" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Office Address</h4>
                      <p className="text-gray-600">{mockData.contact.address}</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-blue-900" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Phone Number</h4>
                      <a href={`tel:${mockData.contact.phone}`} className="text-amber-600 hover:text-amber-700 font-medium">
                        {mockData.contact.phone}
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-blue-900" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Email Address</h4>
                      <a href={`mailto:${mockData.contact.email}`} className="text-amber-600 hover:text-amber-700 font-medium">
                        {mockData.contact.email}
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="text-blue-900" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Business Hours</h4>
                      <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM</p>
                      <p className="text-gray-600">Sunday: Closed</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-8 text-white">
                <h4 className="text-xl font-bold mb-3">24/7 Emergency Support</h4>
                <p className="text-blue-100 mb-4">
                  For urgent trademark matters or office action deadlines, our emergency support team is available around the clock.
                </p>
                <Button
                  onClick={onContactClick}
                  className="bg-amber-500 hover:bg-amber-600 text-white w-full"
                >
                  Request Emergency Support
                </Button>
              </div>
            </div>

            {/* Quick Contact Form */}
            <div>
              <Card className="p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-blue-900 mb-6">
                  Send Us a Message
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Input
                      placeholder="Your Name"
                      className="w-full"
                      onClick={onContactClick}
                      readOnly
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Email Address"
                      type="email"
                      className="w-full"
                      onClick={onContactClick}
                      readOnly
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Phone Number"
                      type="tel"
                      className="w-full"
                      onClick={onContactClick}
                      readOnly
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Tell us about your trademark needs..."
                      rows={6}
                      className="w-full"
                      onClick={onContactClick}
                      readOnly
                    />
                  </div>
                  <Button
                    onClick={onContactClick}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg font-semibold"
                  >
                    Get Started Now
                  </Button>
                </div>

                <p className="text-sm text-gray-500 mt-4 text-center">
                  By submitting this form, you agree to our Privacy Policy and Terms of Service.
                </p>
              </Card>

              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-6">
                <h4 className="font-bold text-blue-900 mb-2">Free Consultation Available</h4>
                <p className="text-gray-700 text-sm">
                  Schedule a free 15-minute consultation with one of our trademark experts to discuss your registration needs and get a customized filing strategy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
